import { useEffect, useState, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit } from '../../components/icons';
import { Modal } from '../../components/Modal';

interface RuleRow {
  leave_type: string;
  label: string;
  rule: 'none' | 'all' | 'max';
  max_days: number | null;
}

const RULE_LABELS: Record<RuleRow['rule'], string> = {
  none: 'No carry over',
  all: 'Carry over all unused days',
  max: 'Carry over up to a maximum',
};

export function LeaveCarryOverRules() {
  const [items, setItems] = useState<RuleRow[] | null>(null);
  const [editing, setEditing] = useState<RuleRow | null>(null);
  const [form, setForm] = useState<{ rule: RuleRow['rule']; max_days: string }>({ rule: 'none', max_days: '' });
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<RuleRow[]>('/leave-carry-over-rules').then((res) => setItems(res.data));

  useEffect(() => {
    load();
  }, []);

  const openEdit = (item: RuleRow) => {
    setForm({ rule: item.rule, max_days: item.max_days !== null ? String(item.max_days) : '' });
    setErrors({});
    setEditing(item);
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    try {
      await api.put(`/leave-carry-over-rules/${editing.leave_type}`, {
        rule: form.rule,
        max_days: form.rule === 'max' ? form.max_days : null,
      });
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Leave Carry-Over Rules</h1>
      </header>
      <p className="muted">
        How much unused leave, if any, an employee keeps when the year rolls over — one rule per leave type,
        applied regardless of position.
      </p>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Leave Type</th>
                <th>Rule</th>
                <th>Max Days</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.leave_type}>
                  <td>{item.label}</td>
                  <td>{RULE_LABELS[item.rule]}</td>
                  <td className="muted">{item.rule === 'max' ? item.max_days : '—'}</td>
                  <td className="right">
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={`Edit carry-over rule — ${editing.label}`} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Rule *
              <select value={form.rule} onChange={(e) => setForm({ ...form, rule: e.target.value as RuleRow['rule'] })}>
                <option value="none">No carry over</option>
                <option value="all">Carry over all unused days</option>
                <option value="max">Carry over up to a maximum</option>
              </select>
              {errors.rule && <small className="error">{errors.rule[0]}</small>}
            </label>
            {form.rule === 'max' && (
              <label className="span-2">
                Max Days *
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={form.max_days}
                  onChange={(e) => setForm({ ...form, max_days: e.target.value })}
                  required
                />
                {errors.max_days && <small className="error">{errors.max_days[0]}</small>}
              </label>
            )}
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
